import { Box, IconButton, Typography } from "@mui/material"
import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined, LinkOutlined } from "@mui-symbols-material/w300"
import type { ExpandedState, SortingState } from "@tanstack/react-table"
import { getCoreRowModel, getSortedRowModel } from "@tanstack/react-table"
import type { FC, RefObject } from "react"
import { useState } from "react"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { Button } from "@/components/common/Button"
import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import { expandedCellStyles } from "@/components/Tables/Aeros/shared"
import { PermissionActionType } from "@/constants/permissions"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { ProductAlternativesPanel } from "@/screens/CustomerProducts/components/ProductAlternatives/ProductAlternativesPanel"
import { AssociationLocationDialog } from "@/screens/CustomerProducts/components/ProductApprovalPerLocation/AssociationLocationDialog"
import { RemoveAssociationLocationDialog } from "@/screens/CustomerProducts/components/ProductApprovalPerLocation/RemoveAssociationLocationDialog"
import type { PreferenceAssociationItem } from "@/screens/CustomerProducts/hooks/useProductPreferenceAssociationList"
import { useProductLocationAssociationColumns } from "@/screens/Products/hooks/useProductLocationAssociationColumns"

interface ProductApprovalPerLocationPanelProps {
  associations: PreferenceAssociationItem[]
  companyProductAssociationId: string | null
  companyId: string
  productId: number
  productPublicId?: string
  tableContainerRef: RefObject<HTMLDivElement>
  handleScroll: () => void
  refetch: () => void
}

const ProductApprovalPerLocationPanel: FC<ProductApprovalPerLocationPanelProps> = ({
  associations,
  companyProductAssociationId,
  companyId,
  productId,
  productPublicId,
  tableContainerRef,
  handleScroll,
  refetch,
}) => {
  const { hasPermissions } = usePermissionManager()
  const canEdit = hasPermissions([PermissionActionType.CUSTOMER_MAINTENANCE])

  const [isExpanded, setIsExpanded] = useState(true)
  const [isAssociateDialogOpen, setIsAssociateDialogOpen] = useState(false)
  const [expandedRow, setExpandedRow] = useState<ExpandedState>({})
  const [sorting, setSorting] = useState<SortingState>([])
  const [editingRow, setEditingRow] = useState<PreferenceAssociationItem | undefined>()
  const [removingRow, setRemovingRow] = useState<PreferenceAssociationItem | undefined>()

  const handleEditOpen = (row: PreferenceAssociationItem) => setEditingRow(row)
  const handleEditClose = () => setEditingRow(undefined)
  const handleRemoveOpen = (row: PreferenceAssociationItem) => setRemovingRow(row)
  const handleRemoveClose = () => setRemovingRow(undefined)

  const { columns } = useProductLocationAssociationColumns({
    onEditOpen: handleEditOpen,
    onRemoveOpen: handleRemoveOpen,
  })

  const handleAssociateLocationOpen = () => setIsAssociateDialogOpen(true)

  return (
    <Box className='rounded-md border border-solid border-gray-300 p-6'>
      <Box className='flex items-center justify-between'>
        <Typography className='text-ml font-normal text-gray-900'>Product Approval Per Location</Typography>
        <Box className='flex items-center gap-2'>
          {isExpanded && canEdit && (
            <Button
              appearance='outlined'
              size='small'
              startIcon={<LinkOutlined />}
              onClick={handleAssociateLocationOpen}
            >
              Associate Location
            </Button>
          )}
          <IconButton size='small' onClick={() => setIsExpanded((prev) => !prev)}>
            {isExpanded ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
          </IconButton>
        </Box>
      </Box>

      {isExpanded && (
        <Box className='pt-4'>
          {associations.length === 0 ? (
            <InformationMissingAlert
              title='Start by associating a location'
              action={
                canEdit && (
                  <Button
                    color='inherit'
                    size='small'
                    appearance='outlined'
                    className='text-primary'
                    startIcon={<LinkOutlined />}
                    onClick={handleAssociateLocationOpen}
                  >
                    Associate Location
                  </Button>
                )
              }
            />
          ) : (
            <Box ref={tableContainerRef} onScroll={handleScroll} className='overflow-y-auto'>
              <AerosBaseTable
                features={{
                  tanstackOptions: {
                    data: associations,
                    columns,
                    state: { sorting, expanded: expandedRow },
                    getCoreRowModel: getCoreRowModel(),
                    getSortedRowModel: getSortedRowModel(),
                    enableSorting: true,
                    enableMultiSort: false,
                    enableSortingRemoval: false,
                    onSortingChange: setSorting,
                    onExpandedChange: setExpandedRow,
                    getRowId: (row) => row.companyProductAssociationLocationId ?? row.companyProductAssociationId,
                    getRowCanExpand: (row) => !!row.original.companyProductAssociationLocationId,
                    renderExpandedContent: (row) => (
                      <ProductAlternativesPanel
                        companyProductAssociationLocationId={row.original.companyProductAssociationLocationId!}
                        shipToLocation={row.original.shipToLocation}
                        productPublicId={productPublicId}
                      />
                    ),
                  },
                }}
                slotProps={{
                  bodyCellExpanded: { className: "bg-primary-100" },
                  expandedCell: expandedCellStyles,
                }}
                noDataMessage='No locations found'
              />
            </Box>
          )}
        </Box>
      )}

      {isAssociateDialogOpen && companyProductAssociationId && (
        <AssociationLocationDialog
          companyProductAssociationId={companyProductAssociationId}
          companyId={companyId}
          productId={productId}
          customerCodes={associations[0]?.customerCode ?? []}
          onClose={() => setIsAssociateDialogOpen(false)}
          onSuccess={() => {
            refetch()
            setIsAssociateDialogOpen(false)
          }}
        />
      )}

      {editingRow?.companyProductAssociationLocationId && companyProductAssociationId && (
        <AssociationLocationDialog
          companyProductAssociationId={companyProductAssociationId}
          companyId={companyId}
          productId={productId}
          customerCodes={associations[0]?.customerCode ?? []}
          onClose={handleEditClose}
          onSuccess={() => {
            refetch()
            handleEditClose()
          }}
          location={{
            companyProductAssociationLocationId: editingRow.companyProductAssociationLocationId,
            shipToLocationId: editingRow.shipToLocationId ?? "",
            shipToLocationName: editingRow.shipToLocation?.name ?? editingRow.shipToLocationId ?? "",
            customerCodes: editingRow.locationCustomerCode ?? [],
            priorityStatus: editingRow.priorityStatus,
            approvalStatus: editingRow.approvalStatus,
          }}
        />
      )}

      {removingRow?.companyProductAssociationLocationId && (
        <RemoveAssociationLocationDialog
          companyProductAssociationLocationId={removingRow.companyProductAssociationLocationId}
          onClose={handleRemoveClose}
          onSuccess={() => {
            refetch()
            handleRemoveClose()
          }}
        />
      )}
    </Box>
  )
}

export default ProductApprovalPerLocationPanel
